import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useFeedbackContext } from '@/contexts/FeedbackContext'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { UserCreateOrEditPendingComponent } from '@/features/admin/users/create-edit/components/UserCreateOrEditPendingComponent'
import { UserForm } from '@/features/admin/users/create-edit/components/UserForm'
import { userFormOptions } from '@/features/admin/users/create-edit/helper/userFormOptions'
import { useCreateUser } from '@/features/admin/users/create-edit/mutations/useCreateUser'
import {
    businessLocationsQueryOptions,
    useBusinessLocations,
} from '@/features/admin/users/create-edit/queries/useBusinessLocations'
import { useUserCreateSchema } from '@/features/admin/users/create-edit/schemas/userCreateOrEditSchema'
import {
    useWorkingTimeModels,
    workingTimeModelsQueryOptions,
} from '@/features/admin/working-time-models/overview/queries/useWorkingTimeModels'
import { BusinessLocationSortBy } from '@/generated/Enumerations/BusinessLocationSortBy_pb'
import { BusinessSortBy } from '@/generated/Enumerations/BusinessSortBy_pb'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { ProjectSortBy } from '@/generated/Enumerations/ProjectSortBy_pb'
import { ProjectFiltersDto } from '@/generated/Messages/Filters_pb'
import { stringifyId } from '@/helper/id'
import { useAppForm } from '@/hooks/useAppForm'
import { businessesQueryOptions, useBusinesses } from '@/queries/useBusinesses'
import { projectsQueryOptions, useProjects } from '@/queries/useProjects'
import { userRolesOptions, useUserRoles } from '@/queries/useUserRoles'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'create-user'

const RouteComponent: React.FC = () => {
    const {
        data: { roles },
    } = useUserRoles()
    const {
        data: { projects },
    } = useProjects(projectsFilters, true)
    const {
        data: { workingTimeModels },
    } = useWorkingTimeModels()
    const {
        data: { businesses },
    } = useBusinesses({
        sorting: [{ sortBy: BusinessSortBy.NAME }],
        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
    })
    const {
        data: { businessLocations },
    } = useBusinessLocations({
        sorting: [{ sortBy: BusinessLocationSortBy.NAME }],
        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
    })

    const { t } = useTranslation()

    const createUser = useCreateUser()
    const userCreateSchema = useUserCreateSchema()
    const { addFeedback } = useFeedbackContext()
    const form = useAppForm({
        ...userFormOptions(),

        validators: {
            onSubmit: userCreateSchema,
        },

        onSubmitInvalid: () => {
            addFeedback?.({
                variant: 'snackbar',
                severity: 'warning',
                title: t('snackbar.failedValidation'),
            })
        },

        onSubmit: async ({ value, formApi }) => {
            await createUser.mutateAsync(value)

            formApi.reset()
        },
    })

    return (
        <>
            <title>{t('user.urlNew')}</title>

            <form.AppForm>
                <form.AdminForm
                    title={t('user.create')}
                    backTo={'/admin/users'}
                >
                    <form.Blocker data-selenium={`${seleniumPrefix}-blocker`} />

                    <UserForm
                        form={form}
                        projects={projects}
                        roles={roles}
                        workingTimeModels={workingTimeModels}
                        businessLocations={businesses.flatMap(b =>
                            businessLocations
                                .filter(
                                    bl =>
                                        stringifyId(bl.businessId) ===
                                        stringifyId(b.id)
                                )
                                .map(bl => ({ ...bl, business: b }))
                        )}
                    />
                </form.AdminForm>
            </form.AppForm>
        </>
    )
}

const projectsFilters: Partial<OmitDeepMessageFields<ProjectFiltersDto>> = {
    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
    sorting: [{ sortBy: ProjectSortBy.NAME }],
}
const PendingComponent: React.FC = () => {
    const { t } = useTranslation()
    return <UserCreateOrEditPendingComponent title={t('user.create')} />
}

export const Route = createFileRoute('/__base-layout/admin/users/create')({
    component: RouteComponent,
    beforeLoad: async ({ context: { user, grpcClients, queryClient } }) => {
        assertUserIsAdminOrRedirect(user)

        await Promise.all([
            queryClient.ensureQueryData(userRolesOptions(grpcClients)),
            queryClient.ensureQueryData(
                projectsQueryOptions(grpcClients, projectsFilters, true)
            ),
            queryClient.ensureQueryData(
                workingTimeModelsQueryOptions(grpcClients)
            ),
            queryClient.ensureQueryData(businessesQueryOptions(grpcClients)),
            queryClient.ensureQueryData(
                businessLocationsQueryOptions(grpcClients)
            ),
        ])
    },
    pendingComponent: PendingComponent,
})
